import { useEffect, useState, type ReactNode } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import { VideoPlayer } from '@/components/VideoPlayer';
import { StreamingMode } from '@/types/streaming';
import { useSourcesStore } from '@/stores';
import { AutoHideOnScroll, ConfidenceBadge, toast } from '@/components';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { EventInfoPanel, EventViewSkeleton } from '../EventView/components';
import { getSourceCacheKey } from '@/constants/cache-keys';
import { AppRoutes } from '@/constants/routes';
import type { Event } from '@/types/event';
import { RangeOrigin } from '@/types/video-player';
import { getTimeDomain } from '@/utils/event-playback-range';

interface CustomEventViewProps {
    event: Event;
    title: string;
    confidence?: number;
    videoStartSeconds?: number | null;
    videoEndSeconds?: number | null;
    onEventLoaded?: (title: string) => void;
    actionBar?: ReactNode;
}

export function CustomEventView({
    event,
    title,
    confidence,
    videoStartSeconds,
    videoEndSeconds,
    onEventLoaded,
    actionBar,
}: CustomEventViewProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const { getSourceById, addSource } = useSourcesStore();

    const cachedSource = getSourceById(event.sourceId);

    const {
        data: fetchedSource,
        error,
        isLoading: sourceLoading,
    } = useSWR(!cachedSource ? getSourceCacheKey(event.sourceId) : null, () =>
        EngineService.getSource(event.sourceId),
    );

    useEffect(() => {
        if (fetchedSource) {
            addSource(fetchedSource);
        }
    }, [fetchedSource, addSource]);

    const source = cachedSource || fetchedSource;

    useEffect(() => {
        if (error) {
            toast.error(t('page.failedToLoad', { type: 'event' }));
            return;
        }
        if (source && onEventLoaded) {
            onEventLoaded(event.description || source.displayName);
        }
    }, [error, source, event.description, onEventLoaded, t]);

    if (sourceLoading) {
        return <EventViewSkeleton />;
    }

    if (!source) {
        return <Navigate to={AppRoutes.EVENTS} replace />;
    }

    const trimStart = videoStartSeconds ?? 0;
    const trimEnd = videoEndSeconds ?? 0;
    // A custom event carries only its chosen thumbnail in `frames`, so its length
    // has to come from the trimmed video range instead.
    const durationSeconds =
        videoStartSeconds != null && videoEndSeconds != null
            ? videoEndSeconds - videoStartSeconds
            : undefined;

    return (
        <div className="h-full relative overflow-hidden pt-14">
            {actionBar && (
                <AutoHideOnScroll container={scrollContainer}>
                    {actionBar}
                </AutoHideOnScroll>
            )}
            <div className="h-full flex flex-col">
                <div
                    ref={setScrollContainer}
                    className="flex-1 overflow-y-auto scrollbar-hide"
                >
                    <div className="flex flex-col gap-6 py-8 max-w-content mx-auto">
                        <VideoPlayer
                            src={source.src || ''}
                            source={source}
                            initialMode={StreamingMode.VOD}
                            fullScreenTitle={source.displayName}
                            controls
                            crop
                            defaultLoop={true}
                            startTime={trimStart}
                            eventRange={{
                                domain: getTimeDomain(source.type),
                                origin: RangeOrigin.SELECTION,
                                start: trimStart,
                                end: trimEnd,
                                playbackStart: trimStart,
                            }}
                        />
                        <div>
                            {confidence !== undefined && (
                                <ConfidenceBadge
                                    className="mb-2"
                                    value={confidence}
                                />
                            )}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <h4 className="text-h4 text-white-100 line-clamp-2 w-fit max-w-full">
                                        {title}
                                    </h4>
                                </TooltipTrigger>
                                <TooltipContent side="top" sideOffset={8}>
                                    {title}
                                </TooltipContent>
                            </Tooltip>
                            {event.description ? (
                                <p className="text-md text-grey-100 mt-1 whitespace-pre-wrap">
                                    {event.description}
                                </p>
                            ) : null}
                        </div>

                        <EventInfoPanel
                            event={event}
                            source={source}
                            durationSeconds={durationSeconds}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
